// ============================================================================
// File: src/miniapps/lex-screenshot-notebook/LexScreenshotNotebook.jsx
// Share logic fixed (uid in URL), read-only when viewing others,
// no crash on undefined uid. 2025-07-22
// ============================================================================
import React, { useState, useCallback, useEffect } from 'react';
import './Lex-screenshot-organizer.css';
import { useAuth } from '../../user/AuthContext';
import {
  loadScreenshots,
  addScreenshot,
  loadCollections,
  addCollection as fbAddCollection,
} from '../../firebase';

import { db } from '../../firebase';
import { deleteDoc, doc, updateDoc, arrayUnion } from 'firebase/firestore';

import { uploadScreenshotFile } from '../../firebase';
import LexLoginSignupPopup from '../lex-loginsignup-popup/lex-loginsignup-popup';

const API = process.env.REACT_APP_API_URL;

// ──────────────────────────────────────────────
// Screen capture hook
// ──────────────────────────────────────────────
const useCapture = () => {
  return useCallback(async onCapture => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const [track] = stream.getVideoTracks();
      const bitmap = await new ImageCapture(track).grabFrame();
      stream.getTracks().forEach(t => t.stop());

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0);

      canvas.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], `window-shot-${Date.now()}.png`, { type: 'image/png' });
        onCapture(file);
      }, 'image/png');
    } catch {
      // user cancelled or not supported
    }
  }, []);
};

export default function LexScreenshotNotebook() {
  const { user } = useAuth();
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  // ─── URL params ───
  const params = new URLSearchParams(window.location.search);
  const shareCardsStr = params.get('cards') || '';
  const shareCards = shareCardsStr ? shareCardsStr.split(',') : [];
  const shareColId = params.get('col') || null;
  const ownerUidParam = params.get('uid') || null;

  const isShareMode = !!(shareCards.length || shareColId);
  const viewerUid = user?.uid || null;
  const ownerUid = isShareMode ? ownerUidParam : viewerUid;

  // 没 ownerUid 但又是分享模式，直接空白提示
  const shareUidMissing = isShareMode && !ownerUid;

  const canWrite = !!user && (!isShareMode || viewerUid === ownerUid);

  const [initializing, setInitializing] = useState(true);
  const [view, setView] = useState('insight'); // 'insight' | 'notebook'
  const [filesQueue, setFilesQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [collections, setCollections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [insightFilterIds, setInsightFilterIds] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const captureScreen = useCapture();

  // 当前 Collection 的截图 id 列表
  const currentColIds = selectedCollectionId
    ? collections.find(c => c.id === selectedCollectionId)?.ids || []
    : null;

  // ─── 初始化 ───
  useEffect(() => {
    // 场景：既不是分享也没登录 => 不加载数据，直接弹登录
    if (!isShareMode && !user) {
      setInitializing(false);
      return;
    }
    // 分享模式却没 uid => 无法加载，直接结束
    if (shareUidMissing) {
      setInitializing(false);
      return;
    }

    (async () => {
      try {
        const targetUid = ownerUid; // 确定使用谁的 uid
        let snaps = [];
        let cols = [];

        if (targetUid) {
          snaps = await loadScreenshots(targetUid);
          cols = await loadCollections(targetUid);
        }

        setHistory(
          snaps.map(s => ({
            ...s,
            customMetadata: s.customMetadata || [],
            selected: false,
          }))
        );
        setCollections(cols);

        if (shareCards.length > 0) {
          setInsightFilterIds(shareCards);
          setView('insight');
        } else if (shareColId) {
          // 分享整个 collection
          setSelectedCollectionId(shareColId);
          const col = cols.find(c => c.id === shareColId);
          if (col?.ids?.length) {
            setInsightFilterIds(col.ids);
            setView('insight');
          }
        }
      } catch (e) {
        console.error('Failed to initialize', e);
      } finally {
        setInitializing(false);
      }
    })();
  }, [user, isShareMode, shareUidMissing, ownerUid, shareCardsStr, shareColId]);

  // ─── 上传 & 分析队列处理 ───
  useEffect(() => {
    if (!filesQueue.length || !user || !canWrite) return;
    (async () => {
      setUploading(true);
      for (let file of filesQueue) {
        setStatusMessage(`Uploading "${file.name}"…`);
        let downloadUrl;
        try {
          downloadUrl = await uploadScreenshotFile(user.uid, file);
        } catch (e) {
          console.error('Storage upload failed', e);
          continue;
        }

        const now = new Date();
        const meta = {
          datetime: now.toLocaleString(),
          weekday: now.toLocaleDateString(undefined, { weekday: 'long' }),
          timeZone: now.toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ').pop(),
          city: Intl.DateTimeFormat().resolvedOptions().timeZone.split('/')[1]?.replace('_', ' ') || '',
          country: navigator.language.split('-')[1] || '',
        };

        setStatusMessage(`Analyzing "${file.name}"…`);
        const form = new FormData();
        form.append('image', file);
        let data;
        try {
          const res = await fetch(`${API}/api/screenshot-analyze`, { method: 'POST', body: form });
          if (!res.ok) throw new Error(await res.text());
          data = await res.json();
        } catch (err) {
          console.error('Analyze API failed', err);
          continue;
        }

        let saved;
        try {
          saved = await addScreenshot(user.uid, {
            blobUrl: downloadUrl,
            meta,
            summary: data.summary,
            description: data.description,
            tags: data.tags,
            tasks: data.tasks,
            entities: data.entities,
          });
        } catch (err) {
          console.error('Firestore write failed', err);
          continue;
        }

        const entry = {
          id: saved.id,
          blobUrl: downloadUrl,
          meta,
          customMetadata: [],
          selected: false,
          ...data,
        };
        setHistory(h => [entry, ...h]);
      }

      setFilesQueue([]);
      setUploading(false);
      setStatusMessage('Done!');
      setTimeout(() => setStatusMessage(''), 2000);
    })();
  }, [filesQueue, user, canWrite]);

  // ─── 删除 Collection ───
  const handleDeleteCollection = async (e, colId) => {
    e.stopPropagation();
    if (!canWrite) return;
    if (!window.confirm('Delete this collection?')) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'collections', colId));
      setCollections(cs => cs.filter(c => c.id !== colId));
      if (selectedCollectionId === colId) setSelectedCollectionId(null);
    } catch (err) {
      console.error('delete collection failed', err);
      alert('Failed to delete. Check console.');
    }
  };

  // ──────────────────────────────────────────────
  // Early returns
  // ──────────────────────────────────────────────
  if (initializing) {
    return <div className="status-message">Loading…</div>;
  }

  if (shareUidMissing) {
    return <div className="status-message">Invalid share link (missing uid).</div>;
  }

  if (!user && !isShareMode) {
    // 未登录且不是分享 -> 弹登录
    return (
      <LexLoginSignupPopup
        loginPromptVisible={true}
        setLoginPromptVisible={setLoginPromptVisible}
      />
    );
  }

  // ──────────────────────────────────────────────
  // Main render
  // ──────────────────────────────────────────────
  return (
    <div className="lex-screenshot-notebook">
      {/* Header + Controls */}
      <header className="hero-header">
        <h1>📸 Lex Screenshot Notebook</h1>
        <p>AI-powered screenshot analyzer for instant clarity</p>

        <div className="controls-row">
          {view === 'notebook' && (
            <button onClick={() => setView('insight')}>← Go Back</button>
          )}

          {canWrite && (
            <>
              <button onClick={() => document.getElementById('file-input').click()}>
                + Upload Screenshot
              </button>
              <button
                onClick={() => captureScreen(f => setFilesQueue(q => [...q, f]))}
                disabled={uploading}
              >
                + Capture Window
              </button>
            </>
          )}

          {/* Notebook 切换按钮登录用户也能看自己的/他人的 */}
          <button onClick={() => setView('notebook')}>Notebook</button>

          {view === 'insight' && (
            <button
              onClick={() => {
                const ids = history
                  .filter(en => !insightFilterIds || insightFilterIds.includes(en.id))
                  .map(en => en.id);
                const uidForShare = viewerUid || ownerUid; // 如果自己没登录但看别人，不生成分享
                if (!uidForShare) return;
                setShareUrl(
                  `${window.location.origin}${window.location.pathname}?uid=${uidForShare}&cards=${ids.join(',')}`
                );
                setShowShareModal(true);
              }}
              disabled={!viewerUid} // 未登录观众就别让他生成自己的分享
            >
              Share
            </button>
          )}

          <input
            id="file-input"
            type="file"
            accept="image/*,.pdf"
            multiple
            hidden
            onChange={e => setFilesQueue(q => [...q, ...Array.from(e.target.files)])}
          />
        </div>

        {uploading && <div className="status-message">{statusMessage}</div>}
      </header>

      {/* Insight 列表 */}
      {view === 'insight' && (
        <div className="insight-list">
          {history
            .filter(en => !insightFilterIds || insightFilterIds.includes(en.id))
            .map(en => (
              <section key={en.id} className="insight-card">
                <img src={en.blobUrl} alt="screenshot" className="thumb" />
                <div className="insight-details">
                  <div className="screenshot-meta">
                    {en.meta.datetime} | {en.meta.city}, {en.meta.country} | {en.meta.weekday}{' '}
                    {en.meta.timeZone}
                  </div>
                  <div>
                    <strong>Summary:</strong> {en.summary}
                  </div>
                  <div>
                    <strong>Description:</strong> {en.description}
                  </div>
                  <div className="tags">
                    <strong>Tags:</strong>{' '}
                    {(en.tags || []).map(t => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="tasks">
                    <strong>Tasks:</strong>
                    <ol className="tasks-list">
                      {(en.tasks || []).map((t, i) => (
                        <li key={i}>{t.task}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="entities">
                    <strong>Entities:</strong>{' '}
                    {(en.entities || []).map((e, i) => (
                      <span key={i} className="entity">
                        {e.kind}: {e.value}
                      </span>
                    ))}
                  </div>

                  {canWrite && (
                    <button
                      onClick={() => {
                        setHistory(h =>
                          h.map(x =>
                            x.id === en.id
                              ? {
                                  ...x,
                                  customMetadata: [
                                    ...x.customMetadata,
                                    { id: Date.now() + Math.random(), short: '', long: '', dirty: false },
                                  ],
                                }
                              : x
                          )
                        );
                      }}
                    >
                      + Add metadata
                    </button>
                  )}

                  {en.customMetadata.map(md => (
                    <div key={md.id} className="metadata-row">
                      <input
                        type="text"
                        placeholder="Key"
                        value={md.short}
                        onChange={e => {
                          if (!canWrite) return;
                          setHistory(h =>
                            h.map(x =>
                              x.id === en.id
                                ? {
                                    ...x,
                                    customMetadata: x.customMetadata.map(m =>
                                      m.id === md.id ? { ...m, short: e.target.value, dirty: true } : m
                                    ),
                                  }
                                : x
                            )
                          );
                        }}
                        disabled={!canWrite}
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={md.long}
                        onChange={e => {
                          if (!canWrite) return;
                          setHistory(h =>
                            h.map(x =>
                              x.id === en.id
                                ? {
                                    ...x,
                                    customMetadata: x.customMetadata.map(m =>
                                      m.id === md.id ? { ...m, long: e.target.value, dirty: true } : m
                                    ),
                                  }
                                : x
                            )
                          );
                        }}
                        disabled={!canWrite}
                      />
                      {md.dirty && canWrite && (
                        <button
                          onClick={() => {
                            setHistory(h =>
                              h.map(x =>
                                x.id === en.id
                                  ? {
                                      ...x,
                                      customMetadata: x.customMetadata.map(m =>
                                        m.id === md.id ? { ...m, dirty: false } : m
                                      ),
                                    }
                                  : x
                              )
                            );
                          }}
                        >
                          Save
                        </button>
                      )}
                      {canWrite && (
                        <button
                          onClick={() => {
                            setHistory(h =>
                              h.map(x =>
                                x.id === en.id
                                  ? {
                                      ...x,
                                      customMetadata: x.customMetadata.filter(m => m.id !== md.id),
                                    }
                                  : x
                              )
                            );
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}

                  <div className="actions">
                    <button onClick={() => setView('notebook')}>Open in Notebook</button>
                    {canWrite && (
                      <button
                        onClick={async () => {
                          await deleteDoc(doc(db, 'users', user.uid, 'screenshots', en.id));
                          setHistory(h => h.filter(x => x.id !== en.id));
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </section>
            ))}
        </div>
      )}

      {/* Notebook View */}
      {view === 'notebook' && (
        <>
          <div className="notebook-search">
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="notebook-main">
            <aside className="sidebar">
              <button
                className={!selectedCollectionId ? 'active' : ''}
                onClick={() => setSelectedCollectionId(null)}
              >
                All Screenshots
              </button>

              {canWrite && (
                <button
                  onClick={async () => {
                    const name = prompt('Collection name');
                    if (name) {
                      const col = await fbAddCollection(user.uid, name);
                      setCollections(c => [col, ...c]);
                    }
                  }}
                >
                  + Add Collection
                </button>
              )}

              {collections.map(col => (
                <div
                  key={col.id}
                  className={`collection-item${col.id === selectedCollectionId ? ' active' : ''}`}
                  onClick={() => setSelectedCollectionId(col.id)}
                >
                  <span className="col-name">{col.name}</span>
                  {canWrite && (
                    <button
                      className="col-delete"
                      onClick={e => handleDeleteCollection(e, col.id)}
                      aria-label={`Delete ${col.name}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </aside>

            <div className="notebook-content">
              {history.some(en => en.selected) && canWrite && (
                <div className="batch-actions">
                  <button
                    onClick={() => {
                      setInsightFilterIds(history.filter(en => en.selected).map(en => en.id));
                      setView('insight');
                    }}
                  >
                    Insight
                  </button>
                  <button
                    onClick={async () => {
                      await Promise.all(
                        history
                          .filter(item => item.selected)
                          .map(item => deleteDoc(doc(db, 'users', user.uid, 'screenshots', item.id)))
                      );
                      setHistory(h => h.filter(item => !item.selected));
                    }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => {
                      history.forEach(en => {
                        if (en.selected) {
                          const a = document.createElement('a');
                          a.href = en.blobUrl;
                          a.download = 'screenshot.png';
                          a.click();
                        }
                      });
                    }}
                  >
                    Download
                  </button>
                  <select
                    defaultValue=""
                    onChange={async e => {
                      const colId = e.target.value;
                      e.target.value = '';
                      if (colId === '__new__') {
                        const name = prompt('Collection name');
                        if (name) {
                          const newCol = await fbAddCollection(user.uid, name);
                          setCollections(cols => [newCol, ...cols]);
                        }
                      } else {
                        const colRef = doc(db, 'users', user.uid, 'collections', colId);
                        await updateDoc(colRef, {
                          ids: arrayUnion(...history.filter(en => en.selected).map(en => en.id)),
                        });
                        setCollections(cols =>
                          cols.map(c =>
                            c.id === colId
                              ? {
                                  ...c,
                                  ids: Array.from(
                                    new Set([
                                      ...(c.ids || []),
                                      ...history.filter(en => en.selected).map(en => en.id),
                                    ])
                                  ),
                                }
                              : c
                          )
                        );
                        setHistory(h => h.map(en => ({ ...en, selected: false })));
                      }
                    }}
                  >
                    <option value="" disabled>
                      Move to Collection…
                    </option>
                    <option value="__new__">+ Add Collection</option>
                    {collections.map(col => (
                      <option key={col.id} value={col.id}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      const ids = history
                        .filter(en => !insightFilterIds || insightFilterIds.includes(en.id))
                        .map(en => en.id);
                      const uidForShare = viewerUid;
                      if (!uidForShare) return;
                      setShareUrl(
                        `${window.location.origin}${window.location.pathname}?uid=${uidForShare}&cards=${ids.join(',')}`
                      );
                      setShowShareModal(true);
                    }}
                  >
                    Share
                  </button>
                </div>
              )}

              <div className="mini-grid">
                {history
                  .filter(en => {
                    if (searchTerm) {
                      const hay = [
                        en.summary,
                        ...(en.tags || []),
                        ...(en.customMetadata || []).map(m => m.short + ' ' + m.long),
                        en.ocrText || '',
                      ]
                        .join(' ')
                        .toLowerCase();
                      if (!hay.includes(searchTerm.toLowerCase())) return false;
                    }
                    if (currentColIds && !currentColIds.includes(en.id)) return false;
                    return true;
                  })
                  .map(en => (
                    <div
                      key={en.id}
                      className={`gallery-card mini${en.selected ? ' selected' : ''}`}
                      onClick={() =>
                        setHistory(h =>
                          h.map(x => (x.id === en.id ? { ...x, selected: !x.selected } : x))
                        )
                      }
                    >
                      <img src={en.blobUrl} alt="thumb" />
                      <div className="mini-overlay">
                        <div className="mini-summary">{en.summary}</div>
                        {en.customMetadata[0] && (
                          <div className="mini-meta">
                            {en.customMetadata[0].short}: {en.customMetadata[0].long}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-backdrop">
          <div className="share-modal">
            <h3>Share this Insight View</h3>
            <input
              type="text"
              readOnly
              value={shareUrl}
              onFocus={e => e.target.select()}
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied');
              }}
            >
              Copy Link
            </button>
            <button onClick={() => setShowShareModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
